// hooks/useMembersPageData.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getMemberConfig, MEMBER_TYPES } from '../utils/membersConfig';

const apiKey = process.env.REACT_APP_API_KEY;
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Stable identity for a member row across all five member types.
 * Congress rows carry bio_guide_id; presidents carry president_num;
 * vice presidents and justices fall back to the row id, then the name.
 */
const getMemberKey = (member) => {
  if (member.bio_guide_id) return `bio-${member.bio_guide_id}`;
  if (member.president_num != null) return `pres-${member.president_num}`;
  if (member.id != null) return `id-${member.id}`;
  return `name-${member.full_name || ''}`;
};

const dedupe = (members, seen = new Set()) =>
  members.filter((member) => {
    const key = getMemberKey(member);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

/**
 * Hook for paginated members list with search
 */
const useMembersPageData = (memberType = MEMBER_TYPES.HOUSE) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const abortControllerRef = useRef(null);
  const skipRef = useRef(0);

  const config = getMemberConfig(memberType);

  // Rows requested from the server per page. Congress over-fetches because the
  // chamber filter is applied client-side and discards part of every response.
  const fetchLimit = useMemo(
    () => (config.dataType === 'congress' ? PAGE_SIZE * 2 : PAGE_SIZE),
    [config.dataType]
  );

  const fetchData = useCallback(async (currentSkip = 0, isLoadMore = false, searchQuery = '') => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      let url;

      if (config.dataType === 'executive') {
        url = `${config.apiEndpoint}?order_by=${config.orderBy || 'president_num'}&limit=${fetchLimit}&skip=${currentSkip}`;
      } else if (config.dataType === 'judicial') {
        url = `${config.apiEndpoint}?limit=${fetchLimit}&skip=${currentSkip}`;
      } else {
        url = `${config.apiEndpoint}?limit=${fetchLimit}&skip=${currentSkip}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      let membersData = await response.json();

      // Capture the raw server row count BEFORE any client-side filtering.
      // This is the only number that says whether more rows exist on the server.
      const rawCount = Array.isArray(membersData) ? membersData.length : 0;

      // Filter by chamber for Congress
      if (config.dataType === 'congress' && config.filterField && config.filterValue) {
        membersData = membersData.filter((member) => {
          const latestTerm = member.terms?.[member.terms.length - 1];
          return latestTerm?.[config.filterField] === config.filterValue;
        });
      }

      // Apply search filter (limited to the rows already fetched - see note below)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        membersData = membersData.filter((member) => {
          const name = member.full_name || `${member.first_name} ${member.last_name}`;
          return name.toLowerCase().includes(query);
        });
      }

      // A full page back from the server means there is more behind it.
      setHasMore(rawCount === fetchLimit);

      if (isLoadMore) {
        // Drop anyone already on screen, then drop repeats within this page.
        setData(prev => {
          const seen = new Set(prev.map(getMemberKey));
          return [...prev, ...dedupe(membersData, seen)];
        });
      } else {
        setData(dedupe(membersData));
      }

      setLoading(false);
      setLoadingMore(false);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching members:', err);
      setError(err);
      setLoading(false);
      setLoadingMore(false);
    }
  }, [config, fetchLimit]);

  // Debounce the search term so typing does not fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    skipRef.current = 0;
    setHasMore(true);
    fetchData(0, false, debouncedSearch);
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, debouncedSearch]);

  const loadMore = useCallback(() => {
    // skip counts SERVER rows, so it advances by what was actually requested,
    // not by how many survived the client-side filter.
    const nextSkip = skipRef.current + fetchLimit;
    skipRef.current = nextSkip;
    fetchData(nextSkip, true, debouncedSearch);
  }, [fetchData, fetchLimit, debouncedSearch]);

  const search = useCallback((query) => {
    setSearchTerm(query);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return {
    data,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    search,
    clearSearch,
    searchTerm,
    config,
  };
};

export default useMembersPageData;